// calendarRunway.js — client-side deterministic runway/lead-time intelligence.
// Mirrors backend/calendar_runway.py so the Calendar Intelligence view can render
// the runway, readiness, conflicts and critical path without a backend round-trip.
// Pure functions only — no React, no network, no mutation.

export const DEFAULT_LEAD_DAYS = {
  caterer: 30, venue: 60, photographer: 30, videographer: 30, florist: 21,
  favors: 21, materials: 21, rentals: 14, invitations: 28, cake: 14,
  attire: 30, transportation: 14, music: 30, generic: 7,
};

const KEYWORDS = [
  ['cater', 'caterer'], ['food', 'caterer'], ['menu', 'caterer'],
  ['venue', 'venue'], ['hall', 'venue'],
  ['photo', 'photographer'], ['videograph', 'videographer'], ['video', 'videographer'],
  ['florist', 'florist'], ['flower', 'florist'],
  ['favor', 'favors'], ['material', 'materials'], ['decor', 'materials'], ['décor', 'materials'],
  ['rental', 'rentals'], ['chair', 'rentals'], ['table', 'rentals'],
  ['save the date', 'invitations'], ['invitation', 'invitations'], ['invite', 'invitations'],
  ['cake', 'cake'], ['dessert', 'cake'],
  ['attire', 'attire'], ['dress', 'attire'], ['suit', 'attire'], ['gown', 'attire'],
  ['transport', 'transportation'], ['shuttle', 'transportation'], ['car', 'transportation'],
  ['music', 'music'], ['band', 'music'], ['dj', 'music'],
];

const DAY = 86400000;

export function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : new Date(v.getFullYear(), v.getMonth(), v.getDate());
  const s = String(v).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function categorize(text) {
  if (!text) return 'generic';
  const t = String(text).toLowerCase();
  for (const [kw, cat] of KEYWORDS) if (t.includes(kw)) return cat;
  if (DEFAULT_LEAD_DAYS[t] != null) return t;
  return 'generic';
}

export function leadDaysFor(catOrText, overrides) {
  const cat = DEFAULT_LEAD_DAYS[catOrText] != null ? catOrText : categorize(catOrText);
  const table = { ...DEFAULT_LEAD_DAYS, ...(overrides || {}) };
  return table[cat] != null ? table[cat] : table.generic;
}

export function lastSafeStart(eventDate, leadDays) {
  const ev = toDate(eventDate);
  if (!ev) return null;
  return new Date(ev.getTime() - Math.max(0, leadDays | 0) * DAY);
}

export function daysBetween(a, b) {
  const da = toDate(a), db = toDate(b);
  if (!da || !db) return null;
  return Math.round((db.getTime() - da.getTime()) / DAY);
}

export function readinessPct(items) {
  const list = items || [];
  if (!list.length) return 0;
  const done = list.filter((it) => ['done', 'complete', 'completed'].includes(String(it.status || '').toLowerCase())).length;
  return Math.round((done * 100) / list.length);
}

export function isBlocked(day, blocks) {
  const d = toDate(day);
  if (!d) return false;
  return (blocks || []).some((b) => {
    const start = toDate(b.start || b.date);
    const end = toDate(b.end || b.start || b.date);
    return start && end && start <= d && d <= end;
  });
}

export function detectConflicts(events) {
  const slots = {};
  (events || []).forEach((e) => {
    const d = toDate(e.date);
    const t = (e.start_time || '').trim();
    if (!d || !t) return;
    const key = `${d.toISOString().slice(0, 10)}|${t}`;
    (slots[key] = slots[key] || []).push(e);
  });
  return Object.keys(slots)
    .filter((k) => slots[k].length > 1)
    .map((k) => ({ date: k.split('|')[0], time: k.split('|')[1], events: slots[k] }))
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
}

export function leadTimeRisks(today, eventDate, items, overrides) {
  const t = toDate(today);
  const risks = [];
  (items || []).forEach((it) => {
    const st = String(it.status || '').toLowerCase();
    if (['done', 'complete', 'completed', 'cancelled'].includes(st)) return;
    const cat = it.category || categorize(it.title);
    const ld = leadDaysFor(cat, overrides);
    const lss = lastSafeStart(eventDate, ld);
    if (!lss || !t) return;
    const slack = Math.round((lss.getTime() - t.getTime()) / DAY);
    let sev = null;
    if (slack < 0) sev = 'overdue';
    else if (slack <= 3) sev = 'now';
    else if (slack <= 10) sev = 'soon';
    else return;
    risks.push({ item: it, category: cat, lead_days: ld, last_safe_start: lss, days_of_slack: slack, severity: sev });
  });
  const order = { overdue: 0, now: 1, soon: 2 };
  risks.sort((a, b) => (order[a.severity] - order[b.severity]) || (a.days_of_slack - b.days_of_slack));
  return risks;
}

export function criticalPath(today, eventDate, items, overrides, limit = 3) {
  return leadTimeRisks(today, eventDate, items, overrides).slice(0, limit);
}

export function computeRunway(today, eventDate, { items = [], events = [], overrides } = {}) {
  const t = toDate(today);
  const ev = toDate(eventDate);
  const daysOut = daysBetween(t, ev);
  const pct = readinessPct(items);
  const risks = leadTimeRisks(t, ev, items, overrides);
  const conflicts = detectConflicts(events);
  let onPace = null;
  if (ev && t) {
    const horizon = 120;
    const start = new Date(ev.getTime() - horizon * DAY);
    if (t <= start) onPace = 0;
    else if (t >= ev) onPace = 100;
    else onPace = Math.round(((t.getTime() - start.getTime()) / DAY) * 100 / horizon);
  }
  return {
    daysOut,
    readinessPct: pct,
    onPacePct: onPace,
    behind: onPace != null && pct < onPace,
    leadTimeRiskCount: risks.filter((r) => r.severity === 'overdue' || r.severity === 'now').length,
    conflictCount: conflicts.length,
    criticalPath: criticalPath(t, ev, items, overrides),
    conflicts,
    risks,
  };
}
